/**
 * Global Contact Support modal. Mounts once in Layout and listens for a
 * `guardian:open-support` window event so any page/component can open
 * it without prop drilling. Auto-captures the user's current page URL.
 *
 * After a successful submit, we surface the top 3 Help Centre articles
 * matching the user's subject. Cuts perceived response time and lets
 * users self-resolve while waiting for the support team.
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supportApi, helpApi } from '../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Loader2, Send, CheckCircle2, ChevronRight, BookOpen } from 'lucide-react';

const STOP_WORDS = new Set([
  'the','a','an','and','or','but','is','are','was','were','be','been','being',
  'have','has','had','do','does','did','will','would','could','should','can',
  'may','might','must','shall','of','in','on','to','for','with','about','i',
  'me','my','we','our','you','your','it','this','that','from','as','at','by',
  'how','why','what','when','where','which','who','whom','not','no','yes',
]);

const tokenise = (text) => {
  if (!text) return [];
  return Array.from(
    new Set(
      text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, ' ')
        .split(/\s+/)
        .filter((t) => t.length > 2 && !STOP_WORDS.has(t))
    )
  );
};

export const ContactSupportModal = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState('normal');
  const [submitting, setSubmitting] = useState(false);

  // Post-submit success state — we keep the dialog open and swap content
  // for "Search the Help Centre while you wait" with 3 related articles.
  const [submitted, setSubmitted] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    const handler = () => {
      setSubject('');
      setBody('');
      setSeverity('normal');
      setSubmitted(false);
      setRelated([]);
      setOpen(true);
    };
    window.addEventListener('guardian:open-support', handler);
    return () => window.removeEventListener('guardian:open-support', handler);
  }, []);

  const fetchRelated = async (subjectText, bodyText) => {
    // Try most-specific token first, then fall back to less-specific.
    // Stop as soon as we have ≥3 articles.
    const tokens = [...tokenise(subjectText), ...tokenise(bodyText)];
    if (tokens.length === 0) {
      // No useful keywords — show some defaults from Getting Started.
      try {
        const res = await helpApi.listArticles({ category: 'Getting Started' });
        return res.data.slice(0, 3);
      } catch {
        return [];
      }
    }
    const seen = new Map(); // slug → article
    for (const q of tokens) {
      try {
        const res = await helpApi.listArticles({ q });
        for (const art of res.data) {
          if (!seen.has(art.slug)) seen.set(art.slug, art);
          if (seen.size >= 5) break;
        }
      } catch {
        // Ignore individual query failures; the modal still works.
      }
      if (seen.size >= 5) break;
    }
    return Array.from(seen.values()).slice(0, 3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) {
      toast.error('Please give us a subject and a description');
      return;
    }
    setSubmitting(true);
    try {
      await supportApi.createTicket({
        subject,
        body,
        severity,
        page_url: typeof window !== 'undefined' ? window.location.href : location.pathname,
      });
      // Successful — fetch related articles BEFORE swapping the UI so the
      // user doesn't see a brief empty state.
      const matches = await fetchRelated(subject, body);
      setRelated(matches);
      setSubmitted(true);
      toast.success("Support ticket sent. We'll get back to you shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    if (submitting) return;
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && setOpen(o)}>
      <DialogContent className="sm:max-w-lg" data-testid="contact-support-dialog">
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle>Contact Support</DialogTitle>
              <DialogDescription>
                We'll reply to your account email. The page you're on, your tenant, and your role are
                automatically included so you don't need to repeat them.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="support-subject">Subject</Label>
                <Input
                  id="support-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g. Can't generate work order from PM"
                  required
                  data-testid="support-subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="support-body">What's happening?</Label>
                <Textarea
                  id="support-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={6}
                  placeholder="Tell us what you were doing, what you expected to happen, and what actually happened. Screenshots can be added after we reply."
                  required
                  data-testid="support-body-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Urgency</Label>
                <div className="flex gap-2">
                  {[
                    { id: 'normal', label: 'Normal' },
                    { id: 'urgent', label: 'Urgent (blocking work)' },
                  ].map((opt) => (
                    <Button
                      key={opt.id}
                      type="button"
                      variant={severity === opt.id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSeverity(opt.id)}
                      data-testid={`support-severity-${opt.id}`}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={close} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="support-submit-btn">
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" /> Send to Support
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          // ── Post-submit success view ────────────────────────────────
          <div data-testid="support-success-view">
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                </div>
                <DialogTitle>Your ticket is on its way</DialogTitle>
              </div>
              <DialogDescription>
                We'll reply to your account email shortly. In the meantime, you might find a quick
                answer in the Help Centre.
              </DialogDescription>
            </DialogHeader>

            {related.length > 0 ? (
              <div className="space-y-2 my-4">
                <p className="label-overline text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5" /> While you wait
                </p>
                {related.map((a) => (
                  <button
                    key={a.slug}
                    onClick={() => {
                      setOpen(false);
                      navigate(`/help/${a.slug}`);
                    }}
                    className="w-full text-left p-3 rounded-md border border-border hover:border-primary/40 bg-card hover:bg-secondary/40 transition-colors flex items-start gap-3"
                    data-testid={`support-related-${a.slug}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{a.title}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{a.summary}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground my-4">
                Browse the <button onClick={() => { setOpen(false); navigate('/help'); }} className="text-primary underline">Help Centre</button> any time.
              </p>
            )}

            <DialogFooter>
              <Button
                onClick={() => { setOpen(false); navigate('/help'); }}
                variant="outline"
                data-testid="support-success-browse-help-btn"
              >
                Browse all Help Centre
              </Button>
              <Button onClick={close} data-testid="support-success-close-btn">
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactSupportModal;
